import React from 'react'
import { CreditApplicationStatus } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'

import useGetIsLoggedInAsAuthSigner from '@sputnik/hooks/useGetIsLoggedInAsAuthSigner'
import AccountCreatedBanner from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/ApplicationDetailsTabContent/ActionsNeededSection/AccountCreatedBanner'
import {
  GetCreditApplicationDetails_creditApplication_CreditApplication,
  GetCreditApplicationDetails_creditApplication_CreditApplication_applicationActionItems_ApplicationActionItem,
} from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'

import AwaitingSignatureBanner from './AwaitingSignatureBanner'
import ConfirmIdentityBanner from './ConfirmIdentityBanner'
import CorrectionNeededBanner from './CorrectionNeededBanner'
import FrozenCreditBanner from './FrozenCreditBanner'
import NewCreditLineBanner from './NewCreditLineBanner'
import OfferAcceptedBanner from './OfferAcceptedBanner'
import OfferReadyBanner from './OfferReadyBanner'
import RefreshApplicationBanner from './RefreshApplicationBanner'
import ResumeApplicationBanner from './ResumeApplicationBanner'
import ReviewMigrationBanner from './ReviewMigrationBanner'
import styles from './styles.module.scss'
import UploadDocumentsBanner from './UploadDocumentsBanner'

type ApplicationActionItem =
  GetCreditApplicationDetails_creditApplication_CreditApplication_applicationActionItems_ApplicationActionItem

type ActionItem =
  | 'provide_documents'
  | 'provide_corrections'
  | 'provide_identity'
  | 'review_offer'
  | 'unfreeze_credit'
  | 'refresh_application'
  | 'resume_application'
  | 'review_migration'
  | 'create_password'

export interface ActionsNeededSectionProps {
  creditApplication: GetCreditApplicationDetails_creditApplication_CreditApplication
}

const ActionsNeededSection = ({ creditApplication }: ActionsNeededSectionProps) => {
  const [getClassName, makeTestId] = useNameFormatter('ActionsNeededSection')

  const {
    salesforceCreditId,
    companyDivvyUuid,
    applicationActionItems,
    personaInquiries,
    dsaVersion,
    authorizedSigner,
    status,
    statusCategory,
  } = creditApplication || {}
  const authSignerEmail = authorizedSigner?.email || ''

  const isAwaitingSignature = status === CreditApplicationStatus.AWAITING_SIGNATURE
  const isInProgressOrNotStarted =
    status === CreditApplicationStatus.IN_PROGRESS || status === CreditApplicationStatus.NOT_STARTED

  const { isLoggedInUserAuthorizedSigner } = useGetIsLoggedInAsAuthSigner({
    authSignerEmail,
  })

  const hasActionItem = ({ requested, canBeCompleted = false }: { requested: ActionItem; canBeCompleted?: boolean }) =>
    applicationActionItems?.reduce<ApplicationActionItem | undefined>((prev, curr) => {
      const { action, completedAt } = curr
      if (action === requested && (canBeCompleted ? true : !completedAt)) {
        if (prev && prev.insertedAt > curr.insertedAt) {
          return prev
        }
        return curr
      }
      return prev
    }, undefined)

  const createPasswordActionItem = hasActionItem({ requested: 'create_password', canBeCompleted: true })

  const reviewOfferActionItem = hasActionItem({ requested: 'review_offer', canBeCompleted: true })

  const authSignerInvitationToken = authorizedSigner?.accountSetupInvitation?.token

  return (
    <div
      className={styles[getClassName('banners-container')]}
      data-testid={makeTestId('')}
    >
      {/* Action items */}
      {/* From onboarding */}
      {hasActionItem({ requested: 'refresh_application' }) && (
        <RefreshApplicationBanner salesforceCreditId={salesforceCreditId} />
      )}

      {hasActionItem({ requested: 'resume_application' }) &&
        ((isAwaitingSignature && isLoggedInUserAuthorizedSigner) ||
          (!isAwaitingSignature && !isLoggedInUserAuthorizedSigner) ||
          isInProgressOrNotStarted) && <ResumeApplicationBanner salesforceCreditId={salesforceCreditId} />}

      {hasActionItem({ requested: 'review_migration' }) && (
        <ReviewMigrationBanner
          authorizedSigner={authorizedSigner}
          dsaVersion={dsaVersion}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {createPasswordActionItem &&
        (authSignerInvitationToken ? (
          <AccountCreatedBanner
            authorizedSigner={authorizedSigner}
            hasCreatedPassword={!!createPasswordActionItem.completedAt}
            salesforceCreditId={salesforceCreditId}
          />
        ) : (
          <NewCreditLineBanner />
        ))}
      {/* from compliance */}
      {hasActionItem({ requested: 'provide_corrections' }) && (
        <CorrectionNeededBanner salesforceCreditId={salesforceCreditId} />
      )}
      {hasActionItem({ requested: 'provide_documents' }) && (
        <UploadDocumentsBanner salesforceCreditId={salesforceCreditId} />
      )}
      {hasActionItem({ requested: 'provide_identity' }) && (
        <ConfirmIdentityBanner
          personaInquiries={personaInquiries}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {/* from underwriting */}
      {reviewOfferActionItem && !reviewOfferActionItem?.completedAt && (
        <OfferReadyBanner
          authorizedSignerEmail={authSignerEmail}
          companyDivvyUuid={companyDivvyUuid ?? ''}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {reviewOfferActionItem && !!reviewOfferActionItem?.completedAt && (
        <OfferAcceptedBanner
          salesforceCreditId={salesforceCreditId}
          statusCategory={statusCategory}
        />
      )}
      {hasActionItem({ requested: 'unfreeze_credit' }) && (
        <FrozenCreditBanner
          authSignerEmail={authSignerEmail}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {/* status derived banners */}

      {isAwaitingSignature && !isLoggedInUserAuthorizedSigner && (
        <AwaitingSignatureBanner salesforceCreditId={salesforceCreditId} />
      )}
    </div>
  )
}

export default ActionsNeededSection
